import { GlobalStore } from '@roc/feature-app-core';
import { action, makeObservable, observable, toJS } from 'mobx';
import {
  ADDRESS_KEYS,
  addressLine,
  EntityFieldSchema,
  fieldTypeOf,
  fieldsForDocument,
  borrowerRows,
  statesToCsv,
  statesToList,
} from './entityReviewFields';

export class EntityReviewFormStore {
  private globalStore: GlobalStore;
  sectionType: string | null = null;
  sectionName: string | null = null;
  entityName: string | null = null;
  loading = false;
  items: any[] = [];
  errors: Record<string, string> = {};
  private unmappedItems: any[] = [];

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    makeObservable(this, {
      sectionType: observable,
      sectionName: observable,
      entityName: observable,
      loading: observable,
      reset: action,
      items: observable,
      errors: observable,
      load: action,
      onExpectedChange: action,
      onAddressFieldChange: action,
      onBorrowerFieldChange: action,
    });
  }

  reset() {
    this.sectionType = null;
    this.sectionName = null;
    this.entityName = null;
    this.items = [];
    this.unmappedItems = [];
    this.errors = {};
    this.loading = true;
  }

  load(data: Record<string, any>, documentName: string, editForm: Record<string, any> = {}) {
    this.loading = false;
    this.sectionType = data?.type ?? null;
    this.sectionName = data?.sectionName ?? null;
    this.entityName = editForm?.borrowerEntityName ?? null;
    this.errors = {};
    const extractedItems: any[] = data?.items ?? [];
    const schema = fieldsForDocument(documentName);
    this.items = schema.map(field =>
      this.toItem(
        field,
        extractedItems.find(item => item.itemName === field.itemName),
        editForm?.borrowers
      )
    );
    this.unmappedItems = extractedItems.filter(
      item => !schema.some(field => field.itemName === item.itemName)
    );
  }

  getItem(itemName: string) {
    return this.items.find(item => item.itemName === itemName);
  }

  getExpected(itemName: string) {
    return toJS(this.getItem(itemName)?.expectedData);
  }

  onExpectedChange(itemName: string, value: any) {
    const item = this.getItem(itemName);
    if (!item) return;
    item.expectedData = value;
    this.errors = { ...this.errors, [itemName]: this.validateField(item, value) };
  }

  onAddressFieldChange(itemName: string, key: string, value: any) {
    const next = { ...(this.getExpected(itemName) ?? {}), [key]: value };
    this.onExpectedChange(itemName, { ...next, address: addressLine(next) });
  }

  onBorrowerFieldChange(itemName: string, index: number, key: string, value: any) {
    const rows = [...(this.getExpected(itemName) ?? [])];
    if (!rows[index]) return;
    rows[index] = { ...rows[index], [key]: value };
    this.onExpectedChange(itemName, rows);
  }

  runFormValidationWithMessage = () => {
    const errors: Record<string, string> = {};
    this.items.forEach(item => {
      errors[item.itemName] = this.validateField(item, item.expectedData);
    });
    this.errors = errors;
    if (Object.values(errors).some(Boolean)) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Please fix the validation errors.',
      });
      return false;
    }
    if (this.items.length && this.items.every(item => this.isBlankForSave(item))) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Enter at least one value before saving.',
      });
      return false;
    }
    return true;
  };

  getFormValues = () => ({
    type: this.sectionType,
    sectionName: this.sectionName,
    items: [
      ...toJS(this.items).map(({ required, format, ...item }) => ({
        ...item,
        expectedData:
          fieldTypeOf(item.itemName) === 'states'
            ? statesToCsv(item.expectedData)
            : item.expectedData,
      })),
      ...toJS(this.unmappedItems),
    ],
  });

  private toItem(field: EntityFieldSchema, extractedItem: any, loanBorrowerRows: any[]) {
    if (fieldTypeOf(field.itemName) === 'borrowers') {
      return {
        ...extractedItem,
        itemName: field.itemName,
        itemDisplayName: field.itemDisplayName,
        required: !!field.required,
        format: field.format,
        expectedData: this.seedBorrowerRows(loanBorrowerRows, extractedItem),
      };
    }
    const seed = this.isBlank(extractedItem?.data)
      ? (field.blankWhenNotExtracted ? null : extractedItem?.expectedData)
      : extractedItem?.data;
    return {
      ...extractedItem,
      itemName: field.itemName,
      itemDisplayName: field.itemDisplayName,
      required: !!field.required,
      format: field.format,
      expectedData: this.normalize(field.itemName, seed),
    };
  }

  private seedBorrowerRows(loanBorrowerRows: any[], extractedItem: any) {
    const saved = borrowerRows(extractedItem?.expectedData);
    const extracted = borrowerRows(extractedItem?.data);
    return borrowerRows(loanBorrowerRows).map(row => {
      const percentage = [
        this.findBorrower(saved, row)?.ownershipPercentage,
        row?.ownershipPercentage,
        this.findBorrower(extracted, row)?.ownershipPercentage,
      ].find(value => !this.isBlank(value));
      return percentage === undefined ? row : { ...row, ownershipPercentage: percentage };
    });
  }

  private findBorrower(rows: any[], row: any) {
    const key = this.borrowerKey(row);
    return key ? rows.find(entry => this.borrowerKey(entry) === key) : undefined;
  }

  private borrowerKey(row: any) {
    return String(row?.borrowerName ?? '').trim().toLowerCase();
  }

  private isBlankForSave(item: any) {
    if (fieldTypeOf(item.itemName) === 'borrowers') {
      return borrowerRows(item.expectedData).every(row => this.isBlank(row?.ownershipPercentage));
    }
    return this.isBlank(item.expectedData);
  }

  private isBlank(value: any) {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') {
      return Object.values(value).every(v => v === null || v === undefined || String(v).trim() === '');
    }
    return String(value).trim() === '';
  }

  private validateField(item: any, value: any): string {
    const isBlank =
      value === null || value === undefined || String(value).trim() === '';
    if (isBlank) return item?.required ? 'This field is required' : '';
    const format = item?.format;
    if (format && !format.pattern.test(String(value).trim())) {
      return format.message;
    }
    return '';
  }

  private normalize(itemName: string, value: any) {
    if (fieldTypeOf(itemName) === 'states') {
      return statesToList(value);
    }
    if (fieldTypeOf(itemName) === 'address') {
      const address = value ?? {};
      return ADDRESS_KEYS.reduce(
        (acc, key) => ({ ...acc, [key]: address[key] ?? '' }),
        {}
      );
    }
    if (fieldTypeOf(itemName) === 'borrowers') {
      return Array.isArray(value) ? value : [];
    }
    return value;
  }
}
